import * as React from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import { Button } from '../../lib/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '../../lib/ui/dialog'

export default function SiteInfo({ currentUser }) {
  const [isDialogVisible, setIsDialogVisible] = React.useState(false)

  // Function to toggle the visibility
  const toggleDialog = () => setIsDialogVisible(!isDialogVisible)

  return (
    <div className="top-0 flex items-center justify-center font-roboto">
      {!isDialogVisible && <SiteInfoTab onTabClick={toggleDialog} />}
      {
        <SiteInfoDialog
          currentUser={currentUser}
          isDialogVisible={isDialogVisible}
          toggleDialog={toggleDialog}
        />
      }
    </div>
  )
}

function SiteInfoTab({ onTabClick }) {
  const { t } = useTranslation()

  return (
    <div
      className="site-info-tab flex cursor-pointer items-center justify-center rounded-xl rounded-t-none px-10 pb-2 pt-2 text-center font-bold text-black"
      style={{
        border: '2px solid var(--theme-primary)',
        borderTop: 'none',
      }}
      onClick={onTabClick} // Call onTabClick when this div is clicked
    >
      <QuestionMarkIconSmall />
      {t('info_title')}
      <i className="el-icon-chevron-down mb-1 pl-5"></i>
    </div>
  )
}

function SiteInfoDialog({
  currentUser,
  isDialogVisible,
  toggleDialog,
  children,
}) {
  const { t } = useTranslation()

  let sectionArray = [
    'coach_chat_desc_html',
    'orientation_desc_html',
    'jobsearch_desc_html',
    'resume_desc_html',
    'story_desc_html',
    'network_desc_html',
    'interview_desc_html',
  ]

  const coachChatIndex = sectionArray.indexOf('coach_chat_desc_html')

  if (!currentUser.has_coach_chat || currentUser.gold) {
    sectionArray.splice(coachChatIndex, 1)
  }

  const orientationIndex = sectionArray.indexOf('orientation_desc_html')

  if (currentUser.internal_mobility) {
    sectionArray[orientationIndex] = 'orientation_desc_im_html'
  } else if (currentUser.candidate_care) {
    if (currentUser.td_bank) {
      sectionArray[orientationIndex] = 'orientation_desc_td_bank_html'
    } else {
      sectionArray[orientationIndex] = 'orientation_desc_candidate_care_html'
    }
  } else if (
    currentUser.education &&
    (currentUser.uk || currentUser.brazilian_portuguese)
  ) {
    sectionArray[orientationIndex] = 'orientation_desc_education_only_html'
  }

  const jobSearchIndex = sectionArray.indexOf('jobsearch_desc_html')

  if (currentUser.candidate_care && currentUser.epicor) {
    sectionArray.splice(jobSearchIndex, 1)
  }

  if (currentUser.internal_mobility) {
    if (currentUser.custom_job_sources) {
      sectionArray[jobSearchIndex] = 'jobsearch_desc_im_custom_html'
    } else {
      sectionArray[jobSearchIndex] = 'jobsearch_desc_im_html'
    }
  } else {
    if (currentUser.custom_job_sources) {
      sectionArray[jobSearchIndex] = 'jobsearch_desc_custom_html'
    }
  }

  const myStoryIndex = sectionArray.indexOf('story_desc_html')

  if (currentUser.internal_mobility) {
    sectionArray[myStoryIndex] = 'story_desc_im_html'
  }

  const networkIndex = sectionArray.indexOf('network_desc_html')

  if (currentUser.internal_mobility) {
    sectionArray[networkIndex] = 'network_desc_im_html'
  } else if (currentUser.education) {
    sectionArray[networkIndex] = 'network_desc_education_only_html'
  } else if (currentUser.candidate_care && currentUser.epicor) {
    sectionArray[networkIndex] = 'network_desc_epicor_cc_only_html'
  }

  const sectionElements = sectionArray.map((section) => (
    <div
      key={section} // Added a key for each element for React's list rendering optimization
      className="info-row pb-4"
      dangerouslySetInnerHTML={{ __html: t(section) }}
    />
  ))

  return (
    <Dialog open={isDialogVisible} onOpenChange={toggleDialog}>
      <DialogTrigger asChild className="cursor-pointer">
        {children}
      </DialogTrigger>
      <DialogContent className="site-info-dialog roboto max-w-xl translate-y-[0%]">
        <DialogHeader>
          <QuestionMarkIcon width="67" height="47" />
          <DialogTitle className="pb-4 text-center text-black">
            {t('info_title')}
          </DialogTitle>
          <DialogDescription className="text-neutral-800">
            <div className="info-row pb-4">
              <div>{t('info_p1')}</div>
            </div>
            <div className="info-row pb-4">
              <div>{t('info_p2')}</div>
            </div>
            {sectionElements}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col sm:flex-row sm:justify-around">
          <Button onClick={toggleDialog}>
            {t('info_close')}
            <i className="el-icon-chevron-up mb-1 pl-5"></i>
          </Button>
        </div>
        <DialogClose />
      </DialogContent>
    </Dialog>
  )
}

function QuestionMarkIconSmall() {
  return (
    <svg
      width="25"
      height="18"
      viewBox="0 0 25 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="-mt-1 mr-3"
    >
      <path
        d="M12.768 17.9619C13.5199 17.9619 14.1294 17.3391 14.1294 16.5709C14.1294 15.8027 13.5199 15.1799 12.768 15.1799C12.016 15.1799 11.4065 15.8027 11.4065 16.5709C11.4065 17.3391 12.016 17.9619 12.768 17.9619Z"
        fill="#08467C"
      />
      <path
        d="M20.9016 17.8837C21.6633 17.65 22.473 17.4063 23.0771 16.8507C23.6169 16.3543 23.9813 15.5961 23.7577 14.8516C23.5345 14.1086 22.8661 13.6131 22.1759 13.3667C21.7845 13.2269 21.375 13.1494 20.9657 13.0885C20.5207 13.0223 20.0737 12.968 19.627 12.9158C17.8181 12.7045 16.0023 12.5753 14.1798 12.5284C13.2805 12.5053 12.3814 12.5059 11.482 12.5192C10.6787 12.5311 9.83273 12.5308 9.08757 12.182C8.5081 11.9108 7.98218 11.4173 7.82071 10.7689C7.63433 10.0203 7.97811 9.25801 8.47126 8.71588C8.7312 8.43012 9.03501 8.18961 9.34841 7.96846C9.6884 7.72854 10.0403 7.50561 10.3997 7.29751C11.1077 6.88786 11.8479 6.53923 12.6126 6.25523C12.9898 6.11509 13.3727 5.99418 13.7588 5.88264C14.0849 5.78846 14.412 5.69414 14.7271 5.56582C15.3363 5.31774 15.9158 4.89754 16.0707 4.20865C16.2175 3.55575 15.97 2.84698 15.536 2.36195C15.034 1.80081 14.277 1.55483 13.5512 1.51503C12.7365 1.47034 11.9405 1.67868 11.1747 1.94308C10.3579 2.22509 9.5587 2.54935 8.72466 2.77777C7.89759 3.00406 7.05452 3.16417 6.20311 3.25665C5.78631 3.30199 5.35692 3.35319 4.93764 3.31848C4.58625 3.28939 4.23666 3.1838 3.97769 2.92901C3.75563 2.71055 3.62335 2.4103 3.66883 2.09344C3.67709 2.03589 3.76266 2.06036 3.75445 2.11756C3.69909 2.50326 3.95571 2.85867 4.27193 3.04073C4.6374 3.25114 5.08213 3.25143 5.48902 3.22736C6.37128 3.17292 7.24757 3.0435 8.1088 2.84044C8.95506 2.64296 9.77109 2.36066 10.5862 2.0591C11.3603 1.77269 12.1518 1.49315 12.9788 1.43112C13.6965 1.37729 14.4513 1.4841 15.0765 1.86726C15.626 2.204 16.03 2.76557 16.1587 3.40998C16.2845 4.0397 16.1103 4.67351 15.6437 5.11295C15.1607 5.56793 14.4983 5.76245 13.881 5.9416C13.0981 6.16883 12.332 6.43444 11.5904 6.77931C10.8388 7.1279 10.1179 7.54196 9.43575 8.01693C8.77732 8.47662 8.12754 9.05504 7.91929 9.87514C7.82229 10.2571 7.8402 10.6623 8.00251 11.0231C8.14683 11.344 8.38451 11.6138 8.66139 11.822C9.32202 12.3187 10.1546 12.4206 10.9526 12.4308C11.8489 12.4422 12.7452 12.4113 13.6417 12.4263C15.4688 12.4569 17.2922 12.5706 19.1118 12.7672C19.5677 12.8165 20.0231 12.871 20.4779 12.9307C20.887 12.9844 21.2975 13.0377 21.6997 13.1331C22.4148 13.3027 23.1107 13.6288 23.5552 14.2483C23.7524 14.5168 23.8746 14.835 23.9088 15.1689C23.9399 15.5174 23.8564 15.8665 23.7022 16.1765C23.3608 16.8626 22.7215 17.299 22.0425 17.5858C21.6789 17.7393 21.3017 17.8557 20.9252 17.9712C20.8704 17.988 20.8469 17.9005 20.9016 17.8837Z"
        fill="#7AC04A"
        fillOpacity="0.8"
      />
      <path
        d="M3.72596 2.82732C4.11826 2.82732 4.43629 2.5024 4.43629 2.10159C4.43629 1.70078 4.11826 1.37585 3.72596 1.37585C3.33365 1.37585 3.01562 1.70078 3.01562 2.10159C3.01562 2.5024 3.33365 2.82732 3.72596 2.82732Z"
        fill="#7AC04A"
        fillOpacity="0.8"
      />
      <path
        d="M8.65394 4.20317C9.04624 4.20317 9.36427 3.87825 9.36427 3.47744C9.36427 3.07663 9.04624 2.75171 8.65394 2.75171C8.26163 2.75171 7.9436 3.07663 7.9436 3.47744C7.9436 3.87825 8.26163 4.20317 8.65394 4.20317Z"
        fill="#7AC04A"
        fillOpacity="0.8"
      />
      <path
        d="M12.8273 1.45146C13.2196 1.45146 13.5376 1.12654 13.5376 0.725731C13.5376 0.324921 13.2196 0 12.8273 0C12.435 0 12.1169 0.324921 12.1169 0.725731C12.1169 1.12654 12.435 1.45146 12.8273 1.45146Z"
        fill="#7AC04A"
        fillOpacity="0.8"
      />
      <path
        d="M15.4318 4.53581C15.8241 4.53581 16.1421 4.21089 16.1421 3.81008C16.1421 3.40927 15.8241 3.08435 15.4318 3.08435C15.0395 3.08435 14.7214 3.40927 14.7214 3.81008C14.7214 4.21089 15.0395 4.53581 15.4318 4.53581Z"
        fill="#7AC04A"
        fillOpacity="0.8"
      />
      <path
        d="M11.9098 8.10405C12.3021 8.10405 12.6201 7.77913 12.6201 7.37832C12.6201 6.97751 12.3021 6.65259 11.9098 6.65259C11.5175 6.65259 11.1995 6.97751 11.1995 7.37832C11.1995 7.77913 11.5175 8.10405 11.9098 8.10405Z"
        fill="#7AC04A"
        fillOpacity="0.8"
      />
      <path
        d="M7.44056 9.82756C7.83286 9.82756 8.15089 9.50264 8.15089 9.10183C8.15089 8.70102 7.83286 8.3761 7.44056 8.3761C7.04825 8.3761 6.73022 8.70102 6.73022 9.10183C6.73022 9.50264 7.04825 9.82756 7.44056 9.82756Z"
        fill="#7AC04A"
        fillOpacity="0.8"
      />
      <path
        d="M12.1762 12.5188C12.5685 12.5188 12.8865 12.1939 12.8865 11.7931C12.8865 11.3923 12.5685 11.0674 12.1762 11.0674C11.7838 11.0674 11.4658 11.3923 11.4658 11.7931C11.4658 12.1939 11.7838 12.5188 12.1762 12.5188Z"
        fill="#7AC04A"
        fillOpacity="0.8"
      />
      <path
        d="M17.2667 14.0308C17.659 14.0308 17.9771 13.7059 17.9771 13.3051C17.9771 12.9043 17.659 12.5793 17.2667 12.5793C16.8744 12.5793 16.5564 12.9043 16.5564 13.3051C16.5564 13.7059 16.8744 14.0308 17.2667 14.0308Z"
        fill="#7AC04A"
        fillOpacity="0.8"
      />
      <path
        d="M23.2157 13.6982C23.608 13.6982 23.926 13.3732 23.926 12.9724C23.926 12.5716 23.608 12.2467 23.2157 12.2467C22.8234 12.2467 22.5054 12.5716 22.5054 12.9724C22.5054 13.3732 22.8234 13.6982 23.2157 13.6982Z"
        fill="#7AC04A"
        fillOpacity="0.8"
      />
      <path
        d="M12.6221 2.05638C10.4943 2.07174 8.75205 3.83835 8.72183 6.01211C8.72155 6.03218 8.72142 6.1733 8.72144 6.35787C8.72145 6.62849 8.82668 6.88802 9.01397 7.07939C9.20126 7.27075 9.45528 7.37827 9.72016 7.37831C9.85146 7.37828 9.98148 7.35181 10.1028 7.30043C10.2241 7.24905 10.3343 7.17375 10.427 7.07884C10.5198 6.98393 10.5934 6.87126 10.6436 6.74729C10.6938 6.62331 10.7195 6.49045 10.7194 6.3563C10.7193 6.23075 10.7192 6.14385 10.7192 6.13851C10.7192 5.80256 10.8047 5.47238 10.9674 5.18044C11.1301 4.88851 11.3644 4.64487 11.6473 4.47351C11.9301 4.30215 12.2517 4.20896 12.5805 4.20311C12.9093 4.19726 13.2339 4.27895 13.5224 4.44015C13.8109 4.60134 14.0533 4.83649 14.2259 5.12246C14.3984 5.40843 14.4952 5.73538 14.5066 6.07113C14.5179 6.40688 14.4436 6.73987 14.2908 7.03735C14.138 7.33484 13.912 7.58656 13.6352 7.76776L13.6355 7.76819C13.6355 7.76819 12.214 8.70316 11.7806 9.98577L11.781 9.98586C11.7048 10.2486 11.6662 10.5212 11.6663 10.7953C11.6663 10.9079 11.6728 11.8936 11.6854 12.6936C11.6897 12.9696 11.8002 13.2329 11.9928 13.4265C12.1854 13.6201 12.4448 13.7285 12.715 13.7285C12.8508 13.7285 12.9853 13.701 13.1106 13.6477C13.236 13.5944 13.3498 13.5164 13.4455 13.418C13.5412 13.3196 13.6169 13.2028 13.6683 13.0744C13.7197 12.946 13.7457 12.8085 13.7449 12.6698C13.7405 11.9371 13.7381 11.084 13.7381 11.0221C13.7381 10.2345 14.4818 9.44982 15.0926 8.9381C15.796 8.34884 16.3022 7.54582 16.5055 6.63998C16.5505 6.45348 16.5753 6.26251 16.5795 6.07048C16.5795 5.54088 16.4769 5.01653 16.2777 4.52761C16.0784 4.0387 15.7865 3.5949 15.4186 3.22177C15.0508 2.84865 14.6143 2.55358 14.1343 2.35357C13.6544 2.15356 13.1404 2.05255 12.6221 2.05638Z"
        fill="#08467C"
      />
      <path
        d="M10.0022 6.89566C9.73734 6.89563 9.48332 6.78811 9.29603 6.59674C9.10873 6.40538 9.00351 6.14584 9.00349 5.87522C9.00348 5.69065 9.00361 5.54953 9.00388 5.52946C9.0123 4.89727 9.16813 4.27625 9.45846 3.7179C8.98146 4.38505 8.71918 5.18717 8.70791 6.01329C8.70764 6.03335 8.70751 6.17447 8.70752 6.35904C8.70754 6.62967 8.81276 6.8892 9.00006 7.08056C9.18735 7.27193 9.44137 7.37945 9.70625 7.37948C9.92391 7.3794 10.1356 7.30673 10.3091 7.17252C10.4827 7.03831 10.6086 6.84989 10.6678 6.63589C10.4849 6.80344 10.2479 6.89596 10.0022 6.89566Z"
        fill="#3D3729"
      />
      <path
        d="M12.6899 4.2065C12.9961 4.22145 13.2942 4.3121 13.5585 4.47067C13.8229 4.62924 14.0457 4.851 14.2077 5.1169C14.3697 5.38281 14.4661 5.68491 14.4887 5.99727C14.5112 6.30964 14.4593 6.62293 14.3372 6.91025C14.5138 6.69841 14.6435 6.45018 14.7177 6.18246C14.7918 5.91475 14.8085 5.63385 14.7668 5.35892C14.725 5.08399 14.6257 4.82149 14.4757 4.58931C14.3256 4.35714 14.1283 4.16075 13.8973 4.01354C13.6662 3.86633 13.4068 3.77176 13.1367 3.73627C12.8666 3.70078 12.5922 3.72521 12.3321 3.80789C12.0721 3.89057 11.8326 4.02956 11.6299 4.21539C11.4273 4.40121 11.2662 4.6295 11.1577 4.88469C11.3452 4.65933 11.5809 4.48099 11.8465 4.3634C12.1122 4.24582 12.4007 4.19214 12.6899 4.2065Z"
        fill="#3D3729"
      />
      <path
        d="M12.997 13.2458C12.7268 13.2458 12.4674 13.1374 12.2748 12.9438C12.0821 12.7502 11.9717 12.487 11.9673 12.2109C11.9548 11.4109 11.9483 10.4253 11.9483 10.3126C11.9481 10.0386 11.9868 9.76592 12.063 9.50317L12.0626 9.50308C12.1076 9.37119 12.1619 9.24283 12.2252 9.11902C12.031 9.38343 11.8764 9.67594 11.7666 9.9869L11.767 9.98699C11.6908 10.2497 11.6522 10.5224 11.6523 10.7964C11.6523 10.9091 11.6588 11.8947 11.6714 12.6947C11.6757 12.9708 11.7862 13.234 11.9788 13.4276C12.1714 13.6212 12.4308 13.7296 12.701 13.7296C12.9238 13.7295 13.1406 13.6557 13.3189 13.5191C13.4971 13.3826 13.6272 13.1906 13.6896 12.9721C13.5003 13.1485 13.2532 13.2461 12.997 13.2458Z"
        fill="#3D3729"
      />
      <path
        d="M12.4681 17.6226C12.2747 17.5365 12.1035 17.4057 11.9681 17.2405C11.8326 17.0752 11.7366 16.8801 11.6876 16.6705C11.6386 16.4608 11.638 16.2424 11.6858 16.0325C11.7335 15.8226 11.8284 15.6269 11.9629 15.4608C11.807 15.5771 11.6771 15.726 11.5817 15.8975C11.4864 16.0691 11.4279 16.2593 11.4101 16.4557C11.3923 16.6521 11.4156 16.8502 11.4784 17.0367C11.5412 17.2232 11.6421 17.3939 11.7744 17.5375C11.9068 17.6812 12.0675 17.7944 12.2459 17.8697C12.4243 17.945 12.6164 17.9807 12.8094 17.9743C13.0023 17.968 13.1918 17.9198 13.3651 17.833C13.5384 17.7461 13.6917 17.6226 13.8148 17.4706C13.6229 17.6139 13.3975 17.7034 13.1613 17.73C12.925 17.7567 12.686 17.7196 12.4681 17.6226Z"
        fill="#3D3729"
      />
    </svg>
  )
}

function QuestionMarkIcon() {
  return (
    <div className="flex justify-center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="67"
        height="47"
        viewBox="0 0 67 47"
        fill="none"
        className="question-mark-icon"
      >
        <path
          d="M34.2183 46.9004C36.2335 46.9004 37.8671 45.2743 37.8671 43.2684C37.8671 41.2625 36.2335 39.6364 34.2183 39.6364C32.2032 39.6364 30.5696 41.2625 30.5696 43.2684C30.5696 45.2743 32.2032 46.9004 34.2183 46.9004Z"
          fill="var(--theme-primary)"
        />
        <path
          d="M56.0161 46.6963C58.0573 46.0861 60.2275 45.4498 61.8464 43.9991C63.293 42.7028 64.2696 40.7232 63.6703 38.7791C63.0723 36.839 61.2809 35.5453 59.4313 34.9019C58.3823 34.537 57.2848 34.3345 56.1878 34.1755C54.9952 34.0026 53.7974 33.861 52.6001 33.7246C47.7522 33.1727 42.886 32.8355 38.0017 32.7131C35.5915 32.6527 33.1819 32.6542 30.7714 32.689C28.6187 32.7201 26.3515 32.7193 24.3545 31.8086C22.8015 31.1003 21.392 29.8118 20.9593 28.1186C20.4598 26.1641 21.3811 24.1737 22.7028 22.7581C23.3994 22.0119 24.2136 21.3839 25.0535 20.8065C25.9647 20.18 26.9077 19.5979 27.871 19.0546C29.7685 17.9849 31.7522 17.0746 33.8016 16.3331C34.8125 15.9671 35.8386 15.6514 36.8735 15.3602C37.7473 15.1143 38.6239 14.868 39.4685 14.5329C41.1012 13.8852 42.6541 12.788 43.0692 10.9892C43.4627 9.28444 42.7993 7.43375 41.6363 6.16729C40.2908 4.70209 38.262 4.05979 36.3171 3.95586C34.1335 3.83919 32.0002 4.38318 29.9479 5.07357C27.7589 5.80992 25.6171 6.65659 23.3819 7.25303C21.1653 7.8439 18.9059 8.26196 16.6241 8.50344C15.5071 8.62183 14.3563 8.75552 13.2327 8.66488C12.2909 8.58892 11.354 8.31323 10.66 7.64795C10.0649 7.0775 9.71038 6.29352 9.83225 5.46618C9.85439 5.31591 10.0837 5.3798 10.0617 5.52915C9.91336 6.53626 10.6011 7.46427 11.4486 7.93965C12.428 8.48906 13.6199 8.4898 14.7104 8.42697C17.0748 8.28482 19.4233 7.94688 21.7314 7.41667C23.9993 6.90103 26.1863 6.16391 28.3708 5.37649C30.4455 4.62865 32.5666 3.89875 34.7831 3.73677C36.7063 3.59623 38.7294 3.87512 40.4048 4.87558C41.8773 5.75484 42.9601 7.22117 43.3052 8.90381C43.6423 10.5481 43.1754 12.203 41.925 13.3504C40.6304 14.5385 38.8552 15.0464 37.2009 15.5141C35.1027 16.1075 33.0495 16.801 31.0621 17.7015C29.0477 18.6117 27.1157 19.6929 25.2876 20.9331C23.523 22.1334 21.7816 23.6437 21.2235 25.785C20.9635 26.7825 21.0115 27.8405 21.4465 28.7826C21.8333 29.6203 22.4703 30.325 23.2123 30.8685C24.9828 32.1654 27.2142 32.4316 29.3528 32.4581C31.7548 32.4879 34.1568 32.4072 36.5595 32.4464C41.4562 32.5264 46.3428 32.8231 51.2194 33.3365C52.4413 33.4652 53.6618 33.6075 54.8806 33.7634C55.977 33.9036 57.077 34.043 58.1549 34.292C60.0714 34.7348 61.9364 35.5862 63.1276 37.2038C63.6563 37.9049 63.9838 38.7358 64.0752 39.6077C64.1586 40.5176 63.9349 41.4292 63.5216 42.2386C62.6068 44.0302 60.8934 45.1697 59.0738 45.9184C58.0992 46.3193 57.0883 46.6231 56.0793 46.9247C55.9324 46.9687 55.8695 46.7401 56.0161 46.6963Z"
          fill="var(--theme-secondary-lighter-15)"
          fillOpacity="0.8"
        />
        <path
          d="M9.98584 7.38246C11.0372 7.38246 11.8895 6.53406 11.8895 5.48749C11.8895 4.44093 11.0372 3.59253 9.98584 3.59253C8.93446 3.59253 8.08215 4.44093 8.08215 5.48749C8.08215 6.53406 8.93446 7.38246 9.98584 7.38246Z"
          fill="var(--theme-secondary-lighter-15)"
          fillOpacity="0.8"
        />
        <path
          d="M23.1928 10.975C24.2441 10.975 25.0964 10.1266 25.0964 9.08002C25.0964 8.03346 24.2441 7.18506 23.1928 7.18506C22.1414 7.18506 21.2891 8.03346 21.2891 9.08002C21.2891 10.1266 22.1414 10.975 23.1928 10.975Z"
          fill="var(--theme-secondary-lighter-15)"
          fillOpacity="0.8"
        />
        <path
          d="M34.377 3.78993C35.4283 3.78993 36.2806 2.94153 36.2806 1.89497C36.2806 0.848405 35.4283 0 34.377 0C33.3256 0 32.4733 0.848405 32.4733 1.89497C32.4733 2.94153 33.3256 3.78993 34.377 3.78993Z"
          fill="var(--theme-secondary-lighter-15)"
          fillOpacity="0.8"
        />
        <path
          d="M41.3571 11.8435C42.4084 11.8435 43.2607 10.9951 43.2607 9.94855C43.2607 8.90199 42.4084 8.05359 41.3571 8.05359C40.3057 8.05359 39.4534 8.90199 39.4534 9.94855C39.4534 10.9951 40.3057 11.8435 41.3571 11.8435Z"
          fill="var(--theme-secondary-lighter-15)"
          fillOpacity="0.8"
        />
        <path
          d="M31.918 21.1604C32.9694 21.1604 33.8217 20.312 33.8217 19.2654C33.8217 18.2189 32.9694 17.3705 31.918 17.3705C30.8666 17.3705 30.0143 18.2189 30.0143 19.2654C30.0143 20.312 30.8666 21.1604 31.918 21.1604Z"
          fill="var(--theme-secondary-lighter-15)"
          fillOpacity="0.8"
        />
        <path
          d="M19.9406 25.661C20.9919 25.661 21.8442 24.8126 21.8442 23.7661C21.8442 22.7195 20.9919 21.8711 19.9406 21.8711C18.8892 21.8711 18.0369 22.7195 18.0369 23.7661C18.0369 24.8126 18.8892 25.661 19.9406 25.661Z"
          fill="var(--theme-secondary-lighter-15)"
          fillOpacity="0.8"
        />
        <path
          d="M32.6318 32.6881C33.6832 32.6881 34.5355 31.8397 34.5355 30.7932C34.5355 29.7466 33.6832 28.8982 32.6318 28.8982C31.5805 28.8982 30.7281 29.7466 30.7281 30.7932C30.7281 31.8397 31.5805 32.6881 32.6318 32.6881Z"
          fill="var(--theme-secondary-lighter-15)"
          fillOpacity="0.8"
        />
        <path
          d="M46.2749 36.636C47.3263 36.636 48.1786 35.7876 48.1786 34.741C48.1786 33.6945 47.3263 32.8461 46.2749 32.8461C45.2235 32.8461 44.3712 33.6945 44.3712 34.741C44.3712 35.7876 45.2235 36.636 46.2749 36.636Z"
          fill="var(--theme-secondary-lighter-15)"
          fillOpacity="0.8"
        />
        <path
          d="M62.2184 35.7675C63.2698 35.7675 64.1221 34.9191 64.1221 33.8725C64.1221 32.8259 63.2698 31.9775 62.2184 31.9775C61.167 31.9775 60.3147 32.8259 60.3147 33.8725C60.3147 34.9191 61.167 35.7675 62.2184 35.7675Z"
          fill="var(--theme-secondary-lighter-15)"
          fillOpacity="0.8"
        />
        <path
          d="M33.8273 5.36929C28.1249 5.40939 23.4557 10.0222 23.3747 15.6981C23.374 15.7505 23.3736 16.119 23.3737 16.601C23.3737 17.3076 23.6557 17.9853 24.1576 18.4849C24.6596 18.9846 25.3404 19.2654 26.0502 19.2654C26.4021 19.2654 26.7506 19.1963 27.0756 19.0621C27.4007 18.9279 27.696 18.7313 27.9447 18.4835C28.1934 18.2357 28.3906 17.9415 28.525 17.6178C28.6595 17.2941 28.7285 16.9472 28.7282 16.5969C28.7279 16.269 28.7278 16.0421 28.7278 16.0282C28.7276 15.151 28.9568 14.2888 29.3929 13.5266C29.829 12.7643 30.4569 12.1281 31.2149 11.6807C31.9728 11.2332 32.8348 10.9899 33.7159 10.9746C34.597 10.9594 35.4669 11.1727 36.2401 11.5936C37.0133 12.0145 37.6631 12.6285 38.1256 13.3752C38.588 14.1219 38.8473 14.9756 38.8778 15.8522C38.9083 16.7289 38.709 17.5984 38.2995 18.3752C37.89 19.1519 37.2845 19.8092 36.5424 20.2824L36.5434 20.2835C36.5434 20.2835 32.7337 22.7248 31.5723 26.0738L31.5733 26.074C31.369 26.7601 31.2655 27.472 31.266 28.1876C31.266 28.4817 31.2834 31.0554 31.317 33.1443C31.3287 33.865 31.6247 34.5523 32.1409 35.0578C32.6572 35.5633 33.3524 35.8466 34.0765 35.8464C34.4404 35.8464 34.8007 35.7747 35.1367 35.6356C35.4726 35.4965 35.7776 35.2926 36.0341 35.0357C36.2906 34.7787 36.4936 34.4739 36.6312 34.1386C36.7689 33.8033 36.8387 33.4443 36.8364 33.0821C36.8247 31.169 36.8184 28.9414 36.8184 28.7797C36.8184 26.7233 38.8114 24.6744 40.4484 23.3382C42.3335 21.7996 43.6901 19.7028 44.235 17.3376C44.3555 16.8506 44.422 16.352 44.4332 15.8505C44.4332 14.4677 44.1583 13.0986 43.6244 11.822C43.0904 10.5454 42.308 9.38653 41.3222 8.41227C40.3363 7.438 39.1665 6.66754 37.8802 6.14529C36.5939 5.62303 35.2165 5.3593 33.8273 5.36929Z"
          fill="var(--theme-primary)"
        />
        <path
          d="M26.8061 18.0051C26.0962 18.0051 25.4155 17.7243 24.9135 17.2246C24.4116 16.725 24.1296 16.0473 24.1295 15.3407C24.1295 14.8587 24.1298 14.4903 24.1306 14.4378C24.1531 12.7871 24.5707 11.1656 25.3488 9.70764C24.0705 11.4497 23.3676 13.5441 23.3374 15.7012C23.3366 15.7536 23.3363 16.122 23.3363 16.604C23.3363 17.3106 23.6184 17.9883 24.1203 18.4879C24.6222 18.9876 25.303 19.2684 26.0129 19.2684C26.5962 19.2682 27.1635 19.0785 27.6287 18.728C28.0938 18.3776 28.4313 17.8856 28.5899 17.3269C28.0997 17.7643 27.4644 18.0059 26.8061 18.0051Z"
          fill="#3D3729"
        />
        <path
          d="M34.0089 10.9836C34.8295 11.0226 35.6284 11.2593 36.3369 11.6734C37.0455 12.0874 37.6424 12.6664 38.0766 13.3607C38.5108 14.055 38.7693 14.8439 38.8297 15.6595C38.8902 16.4751 38.7509 17.2931 38.4238 18.0434C38.8969 17.4902 39.2447 16.8421 39.4433 16.143C39.642 15.444 39.6869 14.7106 39.575 13.9927C39.4631 13.2748 39.197 12.5894 38.7949 11.9832C38.3927 11.3769 37.864 10.8641 37.2447 10.4798C36.6254 10.0954 35.9301 9.84842 35.2063 9.75576C34.4824 9.66309 33.747 9.72688 33.0502 9.94277C32.3533 10.1587 31.7115 10.5216 31.1683 11.0068C30.6251 11.492 30.1935 12.0881 29.9027 12.7544C30.4052 12.166 31.0368 11.7003 31.7487 11.3933C32.4607 11.0862 33.2339 10.9461 34.0089 10.9836Z"
          fill="#3D3729"
        />
        <path
          d="M34.8324 34.5862C34.1082 34.5863 33.413 34.3031 32.8967 33.7976C32.3805 33.2921 32.0846 32.6048 32.0728 31.8841C32.0392 29.7952 32.0218 27.2215 32.0218 26.9274C32.0214 26.2118 32.1249 25.4999 32.3291 24.8139L32.3281 24.8136C32.4487 24.4692 32.5944 24.1341 32.7639 23.8108C32.2435 24.5012 31.8292 25.265 31.5349 26.0769L31.5359 26.0772C31.3317 26.7632 31.2282 27.4751 31.2286 28.1907C31.2286 28.4848 31.246 31.0585 31.2796 33.1474C31.2914 33.8682 31.5873 34.5554 32.1035 35.0609C32.6198 35.5664 33.315 35.8497 34.0392 35.8495C34.6363 35.8494 35.2172 35.6566 35.6949 35.3C36.1726 34.9434 36.5213 34.4423 36.6886 33.8717C36.1812 34.3323 35.519 34.5871 34.8324 34.5862Z"
          fill="#3D3729"
        />
        <path
          d="M33.4143 46.0145C32.896 45.7898 32.4372 45.4483 32.0742 45.0168C31.7112 44.5854 31.4539 44.0759 31.3226 43.5285C31.1913 42.9811 31.1896 42.4109 31.3176 41.8627C31.4456 41.3146 31.6999 40.8036 32.0603 40.37C31.6424 40.6737 31.2942 41.0624 31.0388 41.5103C30.7834 41.9582 30.6266 42.455 30.5788 42.9679C30.531 43.4807 30.5934 43.9978 30.7617 44.4848C30.9301 44.9718 31.2005 45.4176 31.5552 45.7926C31.9098 46.1676 32.3406 46.4632 32.8188 46.6598C33.297 46.8565 33.8117 46.9496 34.3288 46.9331C34.846 46.9166 35.3537 46.7907 35.8182 46.564C36.2827 46.3372 36.6935 46.0147 37.0233 45.6178C36.509 45.992 35.9051 46.2255 35.2719 46.2951C34.6387 46.3647 33.9983 46.268 33.4143 46.0145Z"
          fill="#3D3729"
        />
      </svg>
    </div>
  )
}
